/**
 * Centralized API client for consistent API calls across the application
 */

// Get the API base URL from environment variables
const getApiBaseUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL;
  
  if (apiUrl) {
    // Remove trailing slash if present
    return apiUrl.replace(/\/$/, '');
  }
  
  // Fallback for local development
  return 'http://localhost:8080';
};

/**
 * Make an API request with proper error handling
 */
export const apiRequest = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  console.log(`[API] Making request to: ${url}`);
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`[API] Request failed: ${response.status} ${response.statusText}`, errorText);
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
  }

  return response;
};

/**
 * Make a GET request to the API
 */
export const apiGet = async (endpoint: string): Promise<any> => {
  const response = await apiRequest(endpoint, { method: 'GET' });
  return response.json();
};

/**
 * Make a POST request to the API
 */
export const apiPost = async (endpoint: string, data: any): Promise<any> => {
  const response = await apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

/**
 * Get the full API URL for a given endpoint
 */
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
};

/**
 * Get the WebSocket URL from environment variables
 */
export const getWebSocketUrl = (): string => {
  const wsUrl = process.env.REACT_APP_WS_URL;
  
  if (wsUrl) {
    return wsUrl;
  }
  
  // Fallback for local development
  return 'ws://localhost:8082';
};
